import Skeleton from '@material-ui/lab/Skeleton';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { LoadingStateNoData } from '~/src/dash/components/dashboard/dashboard-graph-v2';
import {
  FILTER_PILLS,
  FilterPillIcons,
  FilterPillKey,
  ICustomAPIFilterConfig,
  ICustomFilterConfig,
} from '~/src/dash/components/filters/common';
import { FilterPill } from '~/src/dash/components/filters/FilterPill';
import { FilterPillSingle } from '~/src/dash/components/filters/FilterPillSingle';
import { Col, Cols, spacing, Text, TextButton } from '~/src/dash/publicaUI';
import { IMenuList } from '~/src/dash/types/menuList';
import { LoadingStateHasData, LoadingStateLoading } from '~/src/dash/utils/loading-states';
import { useBaseStore } from '~/src/dash/views/base/contexts';

type IFiltersState = Record<FilterPillKey, IMenuList>;

interface IFilterRowProps {
  cypressParentName: string;
  enableClear: boolean; // true will display the clear filters button
  filtersConfig: ICustomFilterConfig | ICustomAPIFilterConfig;
  handleClearFilters: () => void; // clear all filters function
  handleFilterAccept: (menuTitle: string, list: IMenuList) => void;
}

export const FilterRow = ({
  cypressParentName,
  enableClear,
  filtersConfig,
  handleClearFilters,
  handleFilterAccept,
}: IFilterRowProps) => {
  const filterKeys = Object.keys(filtersConfig) as FilterPillKey[];
  const [loadState, setLoadState] = useState(LoadingStateLoading);
  const [filters, setFilters] = useState({} as IFiltersState);
  const [activateFilterClear, setActivateFilterClear] = useState({ clear: false });
  const { session, user } = useBaseStore();

  // GET ALL FILTERS ON INITIAL LOAD
  useEffect(() => {
    let tempState = {} as Record<FilterPillKey, IMenuList>;
    const apiCalls = [] as Array<Promise<Record<string, IMenuList>>>;

    filterKeys.forEach((key) => {
      const { list, getList } = FILTER_PILLS[key];
      // if the filter requires an api call, load it
      if (getList) {
        apiCalls.push(getList(session, user));
      } else if (list) {
        tempState[key] = FILTER_PILLS[key].list || ({} as IMenuList);
      }
    });
    Promise.all(apiCalls).then((apiFilters) => {
      for (const filter of apiFilters) {
        tempState = { ...tempState, ...filter };
      }
      // if there are selected defaults, make them selected true here before continuing
      filterKeys.forEach((key) => {
        // set all single selects w/o a default to 'show all'
        if (
          FILTER_PILLS[key].isSingleSelect &&
          (!filtersConfig[key].defaultSelected || isEmpty(filtersConfig[key].defaultSelected))
        ) {
          tempState[key]['0'].selected = true;
        }
        if (filtersConfig[key].defaultSelected && !isEmpty(filtersConfig[key].defaultSelected)) {
          const defaultSelected = filtersConfig[key].defaultSelected || [];
          defaultSelected.forEach((id) => {
            if (!isEmpty(tempState[key][id])) {
              tempState[key][id].selected = true;
            }
          });
        }
      });
      unstable_batchedUpdates(() => {
        setLoadState(isEmpty(tempState) ? LoadingStateNoData : LoadingStateHasData);
        setFilters(tempState);
      });
    });
    // TODO: may need to add error handling
  }, []);

  const handleClearFilterPills = () => {
    setActivateFilterClear({ clear: true });
    handleClearFilters();
  };

  return (
    <Cols max largeHorizontalPadding style={{ position: 'relative' }}>
      {/* FILTER LABEL */}
      <Col style={{ minWidth: '104px' }}>
        <Cols
          gap='8px'
          style={{
            position: 'relative',
            alignItems: 'center',
            height: '42px',
          }}
        >
          <Col paddingTop='2px'>
            <img src='/assets/shared/svg/Filter15px.svg' />
          </Col>
          <Col>
            <Text small bold>
              Filters
            </Text>
          </Col>
        </Cols>
      </Col>
      <Col flex>
        <Cols
          wrap
          style={{
            alignItems: 'center',
            height: '100%',
            padding: '5px 8px 5px 0px',
            columnGap: '2px',
            rowGap: spacing.extraSmall,
          }}
        >
          {loadState === LoadingStateLoading && (
            <Skeleton variant='text' animation='wave' width='100%' />
          )}
          {!isEmpty(filters) &&
            loadState !== LoadingStateLoading &&
            filterKeys.map((key) => {
              const { menuTitle, singularString, icon, isSingleSelect, sortMode } =
                FILTER_PILLS[key];
              const pillIcon = icon ? FilterPillIcons[icon] : undefined;
              // @ts-ignore // we ignore this due to using two different types
              const { defaultSelected, isMultiSelectDisabled, hasSelectAll } = filtersConfig[key];
              return (
                <Col>
                  {!isSingleSelect ? (
                    <FilterPill
                      activateFilterClear={activateFilterClear}
                      cypressParentName={cypressParentName}
                      defaultSelected={defaultSelected}
                      handleFilterAccept={handleFilterAccept}
                      hasSelectAll={!!hasSelectAll}
                      icon={pillIcon}
                      isMultiSelectDisabled={!!isMultiSelectDisabled}
                      list={filters[key]}
                      menuTitle={menuTitle}
                      singularTitle={singularString}
                      sortMode={sortMode}
                    />
                  ) : (
                    <FilterPillSingle
                      activateFilterClear={activateFilterClear}
                      cypressParentName={cypressParentName}
                      defaultSelected={defaultSelected}
                      handleFilterAccept={handleFilterAccept}
                      icon={pillIcon}
                      list={filters[key]}
                      menuTitle={menuTitle}
                      singularTitle={singularString}
                    />
                  )}
                </Col>
              );
            })}
          {/* CLEAR ALL */}
          {enableClear && (
            <Col marginLeft='24px'>
              <TextButton size='extraSmall' onClick={handleClearFilterPills}>
                Clear
              </TextButton>
            </Col>
          )}
        </Cols>
      </Col>
    </Cols>
  );
};
